import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import AppShell from './components/AppShell'
import ApkUpdateLauncher from './components/ApkUpdateLauncher'
import UpdateToast from './components/UpdateToast'
import ChatListPage from './pages/ChatListPage'
import ChatPage from './pages/ChatPage'
import FirstFreeLanePage from './pages/FirstFreeLanePage'
import NewCodeConversationPage from './pages/NewCodeConversationPage'
import { syncJournalDeltas, useWsBridge } from './lib/ws'
import { useAppUnreadBadge } from './lib/appBadge'
import { useFoldableViewport } from './store/ui'
import {
  messagesKey,
  preloadMessageReadModel,
  refreshMessageReadModel,
  suspendReadModelWarmers,
  warmSkillIndex,
  warmSkills,
  type Chat,
  type Message,
  type Topic,
} from './lib/api'
import {
  getSnapshotChat,
  getRenderableSnapshotMessages,
  getSnapshotTopics,
  loadLocalChat,
  loadRenderableLocalMessages,
  loadLocalTopics,
  saveLocalChat,
  saveLocalTopics,
  setLocalMessagesSnapshot,
} from './lib/localReadStore'
import { mergeVisibleMessages } from './lib/messageMerge'
import {
  bindNativePushListeners,
  drainPendingNativePushOpens,
  nativeNotificationPrefetchedEventName,
  nativePushOpenEventName,
  syncGrantedNativePushRegistration,
} from './lib/nativePush'
import {
  clearPendingNotificationNavSoon,
  getPendingNotificationNav,
  rememberPendingNotificationNav,
} from './lib/notificationNav'
import { startNotificationOpenTiming } from './lib/notificationOpenDiagnostics'
import {
  backTarget,
  canonicalActiveChatRouteUrl,
  canonicalChatUrl,
  chatTargetFromUrl,
  sameChatTarget,
} from './lib/chatNavigation'
import { isAgentChatApkDownloadUrl } from './lib/apkUpdate'

declare global {
  interface Window {
    __agentchatHandleAndroidBack?: () => 'handled'
    __agentchatOpenWidget?: (route: string) => void
  }
}

const OUTBOX_SENDING_LABEL_TIMEOUT_MS = 30_000

// ── Code-split routed surfaces ────────────────────────────────────────
// ChatList, ChatPage, MessageList and TopicTabs stay eager because they are the
// notification/open hot path. Optional source cards and secondary pages split.
const SearchPage = lazy(() => import('./pages/SearchPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'))
const SkillIndexPage = lazy(() => import('./pages/SkillIndexPage'))
const SchedulesPage = lazy(() => import('./pages/SchedulesPage'))
const RemindersPage = lazy(() => import('./pages/RemindersPage'))
const CommitmentsPage = lazy(() => import('./pages/CommitmentsPage'))
const EventsPage = lazy(() => import('./pages/EventsPage'))
const ArchitectureCampaignsPage = lazy(() => import('./pages/ArchitectureCampaignsPage'))
const LogsPage = lazy(() => import('./pages/LogsPage'))
const DictationProofPage = lazy(() => import('./pages/DictationProofPage'))
const ApkUpdatePage = lazy(() => import('./pages/ApkUpdatePage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const SetupPage = lazy(() => import('./pages/SetupPage'))

/**
 * Suspense fallback that mirrors the boot-splash style so the page swap
 * feels like a continuation of the launch animation, not a fresh load.
 */
function RouteFallback() {
  return (
    <div className="grid place-items-center min-h-[200px]">
      <div
        style={{
          width: 22, height: 22,
          border: '2.5px solid rgba(120,113,108,0.18)',
          borderTopColor: 'var(--boot-accent, #2563eb)',
          borderRadius: '50%',
          animation: 'boot-spin 0.7s linear infinite',
        }}
        aria-label="Loading"
      />
    </div>
  )
}

function RootRedirect() {
  const navigate = useNavigate()
  useEffect(() => {
    let active = true
    fetch('/api/setup/manifest', { credentials: 'include' })
      .then((response) => (response.ok ? response.json() : null))
      .then((data: { edition?: string; ready?: boolean } | null) => {
        if (!active) return
        const target = data?.edition === 'public' && data.ready === false ? '/setup' : '/chats'
        navigate(target, { replace: true })
      })
      .catch(() => {
        if (active) navigate('/chats', { replace: true })
      })
    return () => { active = false }
  }, [navigate])
  return <RouteFallback />
}

export default function App() {
  const loc = useLocation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const onLogin = loc.pathname === '/login'
  const onSetup = loc.pathname === '/setup'
  const onRoot = loc.pathname === '/'
  const onDictationProof = loc.pathname === '/dictation-proof'
  const onApkUpdate = loc.pathname === '/apk-update'
  const onWidgetLaunch = loc.pathname === '/chats/first-free' || loc.pathname.startsWith('/code/new/')
  const onStandalone = onLogin || onSetup || onRoot
  const backgroundServicesPaused = onStandalone || onDictationProof || onApkUpdate || onWidgetLaunch
  // Back belongs to AgentChat's in-app navigation, never to the OS. The native
  // shell (MainActivity) calls this on every hardware/gesture back press and
  // finishes the activity only when we DON'T return 'handled' — so we always
  // return 'handled'. backTarget() decides where "up" is from the live URL; at
  // the root it returns null and we do nothing. Reading window.location at press
  // time keeps rapid consecutive Back presses on the fresh route instead of a
  // stale React closure. The Home button is the only way to leave AgentChat.
  useEffect(() => {
    function handleAndroidBack(): 'handled' {
      const target = backTarget(window.location.pathname, window.location.search)
      if (target) navigate(target, { replace: true })
      return 'handled'
    }
    window.__agentchatHandleAndroidBack = handleAndroidBack
    return () => {
      if (window.__agentchatHandleAndroidBack === handleAndroidBack) {
        delete window.__agentchatHandleAndroidBack
      }
    }
  }, [loc.pathname, loc.search, navigate])

  useEffect(() => {
    function openWidget(route: string) {
      if (/^\/(?:chats\/first-free|code\/new\/(?:codex|claude))\?launch_id=[0-9a-f-]+$/i.test(route)) {
        navigate(route, { replace: true })
      }
    }
    window.__agentchatOpenWidget = openWidget
    return () => { delete window.__agentchatOpenWidget }
  }, [navigate])

  // Open the global WebSocket connection once for the app's lifetime
  // (skip standalone routes — there's no chat to subscribe to yet).
  useWsBridge(!backgroundServicesPaused)
  // Track viewport / foldable state so paneMode='auto' resolves correctly.
  useFoldableViewport()
  // Mirror total unread to the home-screen icon badge + tab title.
  useAppUnreadBadge(!backgroundServicesPaused)

  useEffect(() => {
    if (backgroundServicesPaused) return
    warmSkills(qc)
    warmSkillIndex(qc)
  }, [backgroundServicesPaused, qc])

  useEffect(() => {
    if (onStandalone) return
    bindNativePushListeners()
    void syncGrantedNativePushRegistration().catch(() => undefined)
  }, [onStandalone])

  useEffect(() => {
    if (onStandalone) return
    function expireUnacknowledgedSendingLabels() {
      const scopes = new Map<string, { chatId: string; topicId: string | null }>()
      for (const [queryKey, messages] of qc.getQueriesData<Message[]>({ queryKey: ['messages'] })) {
        if (!Array.isArray(messages)) continue
        let changed = false
        const next = messages.map((message) => {
          const expired =
            message.outbox_pending === true &&
            message.status === 'queued' &&
            !message.outbox_error &&
            Date.now() - message.ts * 1000 >= OUTBOX_SENDING_LABEL_TIMEOUT_MS
          if (!expired) return message
          changed = true
          return {
            ...message,
            status: 'error' as const,
            outbox_error: 'No server acknowledgement yet; saved on this device and retrying',
          }
        })
        if (!changed) continue
        qc.setQueryData<Message[]>(queryKey, next)
        const chatId = typeof queryKey[1] === 'string' ? queryKey[1] : null
        if (!chatId) continue
        const topicId = typeof queryKey[2] === 'string' ? queryKey[2] : null
        scopes.set(`${chatId}\u0000${topicId ?? ''}`, { chatId, topicId })
      }
      for (const { chatId, topicId } of scopes.values()) {
        refreshMessageReadModel(qc, chatId, topicId)
      }
    }
    let timer: number | null = null
    function scheduleNextSendingDeadline() {
      if (timer !== null) window.clearTimeout(timer)
      timer = null
      let nextDelay = Number.POSITIVE_INFINITY
      const now = Date.now()
      for (const [, messages] of qc.getQueriesData<Message[]>({ queryKey: ['messages'] })) {
        if (!Array.isArray(messages)) continue
        for (const message of messages) {
          if (message.outbox_pending !== true || message.status !== 'queued' || message.outbox_error) continue
          nextDelay = Math.min(
            nextDelay,
            Math.max(0, message.ts * 1000 + OUTBOX_SENDING_LABEL_TIMEOUT_MS - now),
          )
        }
      }
      if (!Number.isFinite(nextDelay)) return
      timer = window.setTimeout(() => {
        timer = null
        expireUnacknowledgedSendingLabels()
        scheduleNextSendingDeadline()
      }, nextDelay + 25)
    }
    const unsubscribe = qc.getQueryCache().subscribe((event) => {
      if (event.query.queryKey[0] === 'messages') scheduleNextSendingDeadline()
    })
    expireUnacknowledgedSendingLabels()
    scheduleNextSendingDeadline()
    return () => {
      unsubscribe()
      if (timer !== null) window.clearTimeout(timer)
    }
  }, [onStandalone, qc])

  // Service-worker → SPA navigation bridge. The notificationclick handler
  // in sw.js posts `{type: 'sw-nav', url: '/chats/<id>'}` when Mark taps a
  // push notification. Before this listener existed, the message dropped
  // silently and the SW's fallback `await w.navigate(...)` was hanging
  // ~30s on Android — leaving the user staring at whatever URL the PWA
  // was last on (e.g. /settings). React Router navigate() routes
  // in-process, no reload, instant.
  //
  // Invalidate before navigating: useMessages has staleTime: Infinity and
  // only refetches on actual mount. When a notification reroutes within
  // the same ChatPage instance — same chatId, different ?topic= (Email /
  // X-Links / Todoist topics all hit this),
  // or the exact-same URL — ChatPage doesn't unmount, so the remount-
  // refetch never fires and the cache shows whatever it had before. WS
  // may have been throttled while the PWA was backgrounded, so that
  // cache is missing the message that triggered the push. Invalidating
  // here forces a refetch on the active observer regardless of mount
  // status, so the latest message is on screen by the time Mark looks.
  useEffect(() => {
    if (onStandalone) return
    function primeCacheFromSnapshot(chatId: string, topicId: string | null): void {
      // Sync-prime React Query cache from the in-memory snapshot before the
      // ChatPage mounts. The snapshot is populated by hydrateLocalReadSnapshots()
      // at boot and by saveLocal* on every IDB write — so on a notification tap
      // for an already-cached chat, useMessages / useChat / useTopics see hot
      // data in their first render and the gray-bubble skeleton never appears.
      const snapMessages = getRenderableSnapshotMessages(chatId, topicId)
      if (snapMessages && snapMessages.length > 0) {
        qc.setQueryData<Message[]>(messagesKey(chatId, topicId), snapMessages)
      }
      const snapChat = getSnapshotChat(chatId)
      if (snapChat && !qc.getQueryData<Chat>(['chat', chatId])) {
        qc.setQueryData<Chat>(['chat', chatId], snapChat)
      }
      const snapTopics = getSnapshotTopics(chatId)
      if (snapTopics && snapTopics.length > 0 && !qc.getQueryData<Topic[]>(['topics', chatId])) {
        qc.setQueryData<Topic[]>(['topics', chatId], snapTopics)
      }
    }
    async function primeCacheFromIdb(chatId: string, topicId: string | null): Promise<void> {
      // The service worker writes the final notification bundle to IndexedDB
      // before exposing the notification. A warm PWA lives in a different JS
      // context, so its in-memory snapshot can still be stale. Read that
      // already-downloaded local bundle into React Query before navigating;
      // this is the no-intermediate-bubble guarantee for notification taps.
      await Promise.all([
        loadRenderableLocalMessages(chatId, topicId).then((messages) => {
          if (!messages || messages.length === 0) return
          qc.setQueryData<Message[]>(messagesKey(chatId, topicId), messages)
        }).catch(() => {}),
        loadLocalChat(chatId).then((chat) => {
          if (!chat) return
          if (!qc.getQueryData<Chat>(['chat', chatId])) qc.setQueryData<Chat>(['chat', chatId], chat)
        }).catch(() => {}),
        loadLocalTopics(chatId).then((topics) => {
          if (!topics || topics.length === 0) return
          if (!qc.getQueryData<Topic[]>(['topics', chatId])) qc.setQueryData<Topic[]>(['topics', chatId], topics)
        }).catch(() => {}),
      ])
    }
    function applyPrefetchedOpenBundle(data: {
      chat_id?: unknown
      topic_id?: unknown
      chat?: unknown
      topics?: unknown
      messages?: unknown
    }): void {
      if (typeof data.chat_id !== 'string') return
      const chatId = data.chat_id
      const topicId = (typeof data.topic_id === 'string' && data.topic_id) ? data.topic_id : null
      const chat = data.chat && typeof data.chat === 'object' ? (data.chat as Chat) : null
      const topics = Array.isArray(data.topics) ? (data.topics as Topic[]) : []
      const messages = Array.isArray(data.messages) ? (data.messages as Message[]) : []
      if (chat) {
        void saveLocalChat(chat)
        qc.setQueryData<Chat>(['chat', chatId], chat)
      }
      if (topics.length > 0) {
        void saveLocalTopics(chatId, topics)
        qc.setQueryData<Topic[]>(['topics', chatId], topics)
      }
      if (messages.length > 0) {
        setLocalMessagesSnapshot(chatId, topicId, messages)
        qc.setQueryData<Message[]>(messagesKey(chatId, topicId), (current) =>
          mergeVisibleMessages(current, messages),
        )
        if (topicId) {
          qc.setQueryData<Message[]>(messagesKey(chatId, null), (current) =>
            mergeVisibleMessages(current, messages),
          )
        }
      }
    }
    async function openNotificationUrl(
      rawUrl: string,
      diagnostic?: {
        source?: 'native' | 'service-worker' | 'pending' | 'direct'
        openId?: string | null
        startMs?: number | null
      },
    ) {
      try {
        const target = new URL(rawUrl, window.location.origin)
        if (isAgentChatApkDownloadUrl(target)) {
          window.location.assign(target.toString())
          return
        }
        const targetInfo = chatTargetFromUrl(target)
        const currentInfo = chatTargetFromUrl(new URL(`${loc.pathname}${loc.search}`, window.location.origin))
        const nextUrl = canonicalChatUrl(target)
        const isNotificationOpen = target.searchParams.get('notification_open') === '1'
        if (isNotificationOpen) suspendReadModelWarmers()
        rememberPendingNotificationNav(nextUrl)
        // Prime cache BEFORE navigation. The about-to-mount ChatPage will see
        // hot React Query data and skip the loading skeleton entirely.
        if (targetInfo) {
          primeCacheFromSnapshot(targetInfo.chatId, targetInfo.topicId)
          if (isNotificationOpen) {
            startNotificationOpenTiming({
              openId: diagnostic?.openId ?? null,
              source: diagnostic?.source ?? 'direct',
              chatId: targetInfo.chatId,
              topicId: targetInfo.topicId,
              messageId: targetInfo.messageId,
              startMs: diagnostic?.startMs ?? null,
            })
            await primeCacheFromIdb(targetInfo.chatId, targetInfo.topicId)
          } else {
            void primeCacheFromIdb(targetInfo.chatId, targetInfo.topicId)
          }
        }
        const sameLogicalTarget = sameChatTarget(currentInfo, targetInfo)
        if (sameLogicalTarget && targetInfo) {
          if (!isNotificationOpen) {
            refreshMessageReadModel(qc, targetInfo.chatId, targetInfo.topicId)
            qc.invalidateQueries({ queryKey: ['topics', targetInfo.chatId], exact: true })
            qc.invalidateQueries({ queryKey: ['chat', targetInfo.chatId], exact: true })
          }
          window.dispatchEvent(new CustomEvent('agentchat:notification-open', {
            detail: {
              chatId: targetInfo.chatId,
              topicId: targetInfo.topicId,
              messageId: targetInfo.messageId,
            },
          }))
        }
        const sameUrl = nextUrl === `${loc.pathname}${loc.search}`
        if (!sameUrl) navigate(nextUrl, { replace: true })
      } catch {
        navigate(rawUrl, { replace: true })
      }
    }

    function onSwMessage(e: MessageEvent) {
      const data = e.data
      if (!data) return
      // Notification-tap navigation (existing path).
      if (data.type === 'sw-nav' && typeof data.url === 'string') {
        void openNotificationUrl(data.url, { source: 'service-worker' })
        return
      }
      if (data.type === 'sw-open-bundle-prefetched' && typeof data.chat_id === 'string') {
        applyPrefetchedOpenBundle(data)
        return
      }
      // The service worker can win the race to deliver a durable outbox item.
      // Its successful POST is authoritative even while the page's duplicate
      // foreground request is still pending (or frozen in the background).
      // Reconcile every cached scope containing the stable client id now so a
      // server-accepted prompt can never remain labelled "Sending…".
      if (data.type === 'message-outbox-sent' && typeof data.client_id === 'string') {
        const clientId = data.client_id as string
        const scopes = new Map<string, { chatId: string; topicId: string | null }>()
        for (const [queryKey, messages] of qc.getQueriesData<Message[]>({ queryKey: ['messages'] })) {
          if (!Array.isArray(messages)) continue
          const matches = messages.some((m) => (
            m.id === clientId ||
            m.client_id === clientId ||
            m.external_message_id === clientId
          ))
          if (!matches) continue
          const chatId = typeof queryKey[1] === 'string' ? queryKey[1] : null
          if (!chatId) continue
          const topicId = typeof queryKey[2] === 'string' ? queryKey[2] : null
          scopes.set(`${chatId}\u0000${topicId ?? ''}`, { chatId, topicId })
          qc.setQueryData<Message[]>(queryKey, (current) => current?.map((m) => (
            m.id === clientId ||
            m.client_id === clientId ||
            m.external_message_id === clientId
              ? {
                  ...m,
                  status: m.status === 'error' ? 'queued' : m.status,
                  outbox_pending: false,
                  outbox_error: null,
                }
              : m
          )))
        }
        for (const { chatId, topicId } of scopes.values()) {
          refreshMessageReadModel(qc, chatId, topicId)
          qc.invalidateQueries({ queryKey: ['topics', chatId], exact: true })
          qc.invalidateQueries({ queryKey: ['chat', chatId], exact: true })
        }
        return
      }
      // Push prefetch merged a fresh /messages response into IDB. Funnel the
      // same array straight into the React Query cache + snapshot so an open
      // ChatPage updates in the same frame — no network round-trip required,
      // works even when the WebSocket is silently dead. Mark's invariant: a
      // notification firing while he's in the chat must show the new bubble.
      if (data.type === 'sw-messages-prefetched' && typeof data.chat_id === 'string') {
        const chatId = data.chat_id as string
        const topicId = (typeof data.topic_id === 'string' && data.topic_id) ? data.topic_id : null
        const messages = Array.isArray(data.messages) ? (data.messages as Message[]) : []
        if (messages.length === 0) return
        setLocalMessagesSnapshot(chatId, topicId, messages)
        qc.setQueryData<Message[]>(messagesKey(chatId, topicId), (current) =>
          mergeVisibleMessages(current, messages),
        )
        // Mirror into the no-topic scope too if a topic was scoped — keeps the
        // chat-level view (when Mark backs out of the topic) in lockstep.
        if (topicId) {
          qc.setQueryData<Message[]>(messagesKey(chatId, null), (current) =>
            mergeVisibleMessages(current, messages),
          )
        }
        return
      }
      // Push was suppressed because Mark is already focused on the chat. If
      // the SW-messages-prefetched broadcast above didn't reach (e.g. the SW
      // chose not to prefetch because the chat was already open), tail-merge
      // the newest server rows so the open observer pulls the new bubble
      // without swapping the mounted virtualized transcript.
      if (data.type === 'push-suppressed' && typeof data.chat_id === 'string') {
        const chatId = data.chat_id as string
        const topicId = (typeof data.topic_id === 'string' && data.topic_id) ? data.topic_id : null
        preloadMessageReadModel(qc, chatId, topicId)
      }
    }
    function onNativePushOpen(e: Event) {
      const event = e as CustomEvent<{
        url?: string
        handled?: boolean
        diagnosticOpenId?: string | null
        diagnosticStartMs?: number | null
      }>
      const url = event.detail?.url
      if (url) {
        void openNotificationUrl(url, {
          source: 'native',
          openId: event.detail?.diagnosticOpenId ?? null,
          startMs: event.detail?.diagnosticStartMs ?? null,
        })
      }
      if (event.detail) event.detail.handled = true
      e.preventDefault()
    }
    function onNativeNotificationPrefetched(e: Event) {
      const event = e as CustomEvent<{
        chat_id?: unknown
        topic_id?: unknown
        chat?: unknown
        topics?: unknown
        messages?: unknown
      }>
      if (event.detail) applyPrefetchedOpenBundle(event.detail)
    }
    navigator.serviceWorker?.addEventListener('message', onSwMessage)
    window.addEventListener(nativePushOpenEventName(), onNativePushOpen)
    window.addEventListener(nativeNotificationPrefetchedEventName(), onNativeNotificationPrefetched)
    drainPendingNativePushOpens((url, diagnostic) => {
      void openNotificationUrl(url, {
        source: 'pending',
        openId: diagnostic?.openId ?? null,
        startMs: diagnostic?.startMs ?? null,
      })
    })
    const pendingNotificationUrl = getPendingNotificationNav()
    if (pendingNotificationUrl && pendingNotificationUrl !== `${loc.pathname}${loc.search}`) {
      void openNotificationUrl(pendingNotificationUrl, { source: 'pending' })
    }
    return () => {
      navigator.serviceWorker?.removeEventListener('message', onSwMessage)
      window.removeEventListener(nativePushOpenEventName(), onNativePushOpen)
      window.removeEventListener(nativeNotificationPrefetchedEventName(), onNativeNotificationPrefetched)
    }
  }, [navigate, qc, loc.pathname, loc.search, onStandalone])

  useEffect(() => {
    if (onStandalone) return
    const currentPathAndSearch = `${loc.pathname}${loc.search}`
    const canonicalPathAndSearch = canonicalActiveChatRouteUrl(
      new URL(currentPathAndSearch, window.location.origin),
    )
    if (canonicalPathAndSearch !== currentPathAndSearch) {
      navigate(canonicalPathAndSearch, { replace: true })
    }
  }, [loc.pathname, loc.search, navigate, onStandalone])

  useEffect(() => {
    if (onStandalone) return
    const pendingNotificationUrl = getPendingNotificationNav()
    if (!pendingNotificationUrl || pendingNotificationUrl !== `${loc.pathname}${loc.search}`) return
    clearPendingNotificationNavSoon(pendingNotificationUrl)
  }, [loc.pathname, loc.search, onStandalone])

  // Foreground refresh: catch up server state without dismissing notifications
  // from cached read models. Notification clearing stays tied to explicit
  // read/open events so stale cache can never wipe the tray.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState !== 'visible') return
      if (backgroundServicesPaused) return
      void syncJournalDeltas(qc).then((synced) => {
        if (synced) return
        qc.invalidateQueries({ queryKey: ['chats'] })
        qc.invalidateQueries({ queryKey: ['lanes'] })
        qc.invalidateQueries({ queryKey: ['work-monitor'] })
      })
    }
    onVisible()
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    window.addEventListener('pageshow', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
      window.removeEventListener('pageshow', onVisible)
    }
  }, [backgroundServicesPaused, qc])

  // /login renders without the AppShell wrapper so it gets a clean,
  // full-page presentation (no side rail / bottom nav).
  if (onLogin) {
    return (
      <>
        <ApkUpdateLauncher />
        <Suspense fallback={<RouteFallback />}>
          <LoginPage />
        </Suspense>
      </>
    )
  }
  if (onSetup) {
    return (
      <>
        <ApkUpdateLauncher />
        <Suspense fallback={<RouteFallback />}>
          <SetupPage />
        </Suspense>
      </>
    )
  }
  if (onRoot) {
    return <RootRedirect />
  }

  return (
    <>
      <ApkUpdateLauncher />
      <UpdateToast />
      {onWidgetLaunch ? (
        <Routes>
          <Route path="/chats/first-free" element={<FirstFreeLanePage />} />
          <Route path="/code/new/:agent" element={<NewCodeConversationPage />} />
        </Routes>
      ) : <AppShell>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/chats" element={<ChatListPage />} />
            <Route path="/chats/:chatId" element={<ChatPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/schedules" element={<SchedulesPage />} />
            <Route path="/reminders" element={<RemindersPage />} />
            <Route path="/commitments" element={<CommitmentsPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/architecture-campaigns" element={<ArchitectureCampaignsPage />} />
            <Route path="/logs" element={<LogsPage />} />
            <Route path="/dictation-proof" element={<DictationProofPage />} />
            <Route path="/apk-update" element={<ApkUpdatePage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/skills" element={<SkillIndexPage />} />
            <Route path="*" element={<Navigate to="/chats" replace />} />
          </Routes>
        </Suspense>
      </AppShell>}
    </>
  )
}
